import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, Typography, message } from "@mimber/ui-web"
import { ClockCircleOutlined } from "@mimber/ui-web"
import { Form, DatePickerField } from "@mimber/forms"
import { useScheduleAnnouncement } from "@mimber/client/mimber-organization"
import {
  QuickScheduleAnnouncementFormSchema,
  type QuickScheduleAnnouncementFormData,
} from "./QuickScheduleAnnouncementForm.schema"
import type { QuickScheduleAnnouncementFormProps } from "./QuickScheduleAnnouncementForm.types"

const { Paragraph } = Typography

export const QuickScheduleAnnouncementForm: React.FunctionComponent<
  QuickScheduleAnnouncementFormProps
> = ({ announcement, onSuccess, onCancel }) => {
  const form = useForm<QuickScheduleAnnouncementFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(QuickScheduleAnnouncementFormSchema) as any,
    mode: "onBlur",
    defaultValues: {
      scheduledPublishAt: "",
    },
  })

  const {
    reset,
  } = form

  const mutation = useScheduleAnnouncement({
    onSuccess: () => {
      message.success("Announcement scheduled successfully")
      reset()
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to schedule announcement: ${error.message}`)
    },
  })

  const onSubmit = (data: QuickScheduleAnnouncementFormData) => {
    mutation.mutate({ uuid: announcement.uuid, ...data })
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Paragraph>
          Choose a date and time to automatically publish this announcement.
        </Paragraph>

        <DatePickerField
          name="scheduledPublishAt"
          label="Publish At"
          required
          showTime
        />

        <Space style={{ justifyContent: "flex-end", width: "100%" }}>
          <Button onClick={onCancel} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<ClockCircleOutlined />}
            htmlType="submit"
            loading={mutation.isPending}
          >
            Schedule
          </Button>
        </Space>
      </Space>
    </Form>
  )
}
